import { createContext, useState, useContext, useEffect } from 'react';
import { toast } from 'react-hot-toast';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const stored = localStorage.getItem('cart');
    return stored ? JSON.parse(stored) : [];
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  // ✅ Add or increment item in cart (with size support)
  const addToCart = (product) => {
    setCart((prev) => {
      const existing = prev.find(
        item => item._id === product._id && item.size === product.size
      );

      const matchedStock = product.sizes?.find(s => s.size === product.size)?.stock || 1;

      if (existing) {
        if ((existing.quantity || 1) >= matchedStock) {
          toast.error('Stock limit reached');
          return prev;
        }
        toast.success('Increased quantity');
        return prev.map(item =>
          item._id === product._id && item.size === product.size
            ? { ...item, quantity: (item.quantity || 1) + 1 }
            : item
        );
      } else {
        toast.success('Added to cart!');
        return [...prev, { ...product, quantity: 1 }];
      }
    });
  };

  // ✅ Remove item (with size match)
  const removeFromCart = (productId, size) => {
    setCart((prev) =>
      prev.filter(item => !(item._id === productId && item.size === size))
    );
  };

  // ✅ Update quantity safely (with size & stock check)
  const updateItemQuantity = (productId, newQuantity, size) => {
    if (newQuantity < 1) return;

    setCart((prev) =>
      prev.map(item => {
        if (item._id === productId && item.size === size) {
          const matchedStock = item.sizes?.find(s => s.size === size)?.stock || 1;
          if (newQuantity > matchedStock) {
            toast.error('Stock limit reached');
            return item;
          }
          return { ...item, quantity: newQuantity };
        }
        return item;
      })
    );
  };

  const clearCart = () => setCart([]);

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      removeFromCart,
      updateItemQuantity,
      clearCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
